import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, Quote, Truck, RefreshCw, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 6: DHARTI - Earth Ethnic
 * Style: Warm, earthy, rooted in Indian textile traditions
 * Best for: Handloom sarees, Cotton kurtas, Ethnic wear
 * Warm sand background with burnt umber accents
 */
const Template6 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);

    // Categories
    const categories = [
        { name: 'Handloom Sarees', image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Cotton Kurtas', image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Ethnic Sets', image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Block Print', image: 'https://images.pexels.com/photos/2118563/pexels-photo-2118563.jpeg?auto=compress&cs=tinysrgb&w=600' },
    ];

    // Products
    const products = [
        { id: 1, name: 'Chanderi Silk Saree', price: '\u20B94,999', badge: 'Handloom', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Khadi Cotton Kurta', price: '\u20B92,499', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Jamdani Weave Dupatta', price: '\u20B91,899', badge: 'New', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Block Print Anarkali', price: '\u20B93,999', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Tussar Silk Ensemble', price: '\u20B97,499', badge: 'Artisan', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Ikat Print Palazzo Set', price: '\u20B92,999', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Banarasi Silk Saree', price: '\u20B912,999', badge: 'Premium', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Ajrakh Hand Block Kurta', price: '\u20B9999', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The handloom saree I received is absolutely stunning. You can feel the heritage in every thread. It drapes beautifully and the colours are earthy perfection.',
            name: 'Meera Iyer',
            city: 'Bengaluru'
        },
        {
            text: 'Dharti brings the soul of Indian textiles to your doorstep. My block print kurta is my go-to for every festive occasion. Pure comfort meets tradition.',
            name: 'Ritu Sharma',
            city: 'Jaipur'
        },
        {
            text: 'I have been searching for authentic handloom pieces for years. Dharti exceeded all expectations. The quality of the Chanderi silk is unmatched and the artisan story behind each piece makes it special.',
            name: 'Anita Desai',
            city: 'Ahmedabad'
        }
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    const handleSubscribe = (e) => { e.preventDefault(); if (email.trim()) setEmail(''); };

    return (
        <div style={{ fontFamily: "'Nunito', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Display:wght@400;600;700&family=Nunito:wght@300;400;600&display=swap');
            `}</style>

            {/* ===== SECTION 1: FULL-WIDTH HERO BANNER ===== */}
            <section className="relative w-full min-h-[85vh] lg:min-h-screen overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#3B1A08' }}>
                {/* Background Image */}
                <img
                    src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200"
                    alt="Indian handloom heritage"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ opacity: 0.45 }}
                />

                {/* Warm Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(to bottom, rgba(59, 26, 8, 0.3) 0%, rgba(59, 26, 8, 0.5) 50%, rgba(59, 26, 8, 0.85) 100%)'
                    }}
                />

                {/* Hero Content */}
                <div className="relative z-10 text-center px-5 sm:px-8">
                    <p
                        className="text-xs sm:text-sm tracking-[0.35em] uppercase mb-5"
                        style={{ color: '#D4C5B0', fontFamily: "'Nunito', sans-serif", fontWeight: 300 }}
                    >
                        Rooted in Indian Textile Traditions
                    </p>
                    <h1
                        className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mb-5 tracking-wide"
                        style={{
                            fontFamily: "'Noto Serif Display', Georgia, serif",
                            color: '#F5F0E8',
                            fontWeight: 400,
                            lineHeight: 1.15,
                        }}
                    >
                        Woven With<br />Heritage
                    </h1>
                    <p
                        className="text-sm sm:text-base lg:text-lg font-light mb-8 max-w-md mx-auto"
                        style={{
                            color: '#D4C5B0',
                            fontFamily: "'Nunito', sans-serif",
                            fontWeight: 300,
                            lineHeight: 1.7,
                        }}
                    >
                        Authentic handloom textiles and ethnic wear, crafted by master artisans across India
                    </p>
                    <button
                        onClick={() => navigate('/template6/shop')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                        style={{
                            backgroundColor: '#8B4513',
                            border: '2px solid #8B4513',
                            color: '#FFFFFF',
                            fontFamily: "'Nunito', sans-serif",
                            fontWeight: 600,
                            borderRadius: '2px',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#6B3410';
                            e.currentTarget.style.borderColor = '#6B3410';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#8B4513';
                            e.currentTarget.style.borderColor = '#8B4513';
                        }}
                    >
                        Explore Collection <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                    <div className="w-px h-12 opacity-40" style={{ backgroundColor: '#D4C5B0' }}>
                        <div
                            className="w-full h-1/2"
                            style={{
                                backgroundColor: '#D4C5B0',
                                animation: 'dhartiScrollPulse 2s ease-in-out infinite'
                            }}
                        />
                    </div>
                </div>
                <style>{`
                    @keyframes dhartiScrollPulse {
                        0%, 100% { opacity: 0.3; transform: translateY(0); }
                        50% { opacity: 1; transform: translateY(12px); }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 2: ARTISAN STORY (Image Left + Text Right) ===== */}
            <section style={{ backgroundColor: '#F5F0E8' }} className="py-16 sm:py-20 lg:py-28">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
                    <div className="dharti-story-grid" style={{ display: 'grid', gap: '48px', alignItems: 'center' }}>
                        {/* Image */}
                        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '4px' }}>
                            <img
                                src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="Indian artisan weaving handloom"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: '360px' }}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: '24px',
                                    left: '24px',
                                    backgroundColor: 'rgba(59, 26, 8, 0.85)',
                                    padding: '12px 20px',
                                    borderRadius: '2px',
                                }}
                            >
                                <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '12px', color: '#D4C5B0', letterSpacing: '2px', textTransform: 'uppercase', margin: 0 }}>
                                    Since 1985
                                </p>
                            </div>
                        </div>

                        {/* Text */}
                        <div>
                            <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '12px', fontWeight: 600, color: '#8B4513', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '16px' }}>
                                Our Story
                            </p>
                            <h2
                                style={{
                                    fontFamily: "'Noto Serif Display', Georgia, serif",
                                    fontSize: 'clamp(28px, 4vw, 40px)',
                                    fontWeight: 400,
                                    color: '#2C1810',
                                    lineHeight: 1.3,
                                    marginBottom: '24px',
                                }}
                            >
                                The Handloom Story of India
                            </h2>
                            <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '15px', color: '#7C6B5D', lineHeight: 1.8, marginBottom: '16px', fontWeight: 300 }}>
                                Every thread we weave carries the legacy of generations. Our artisans in remote villages of Varanasi, Chanderi, and Pochampally pour their hearts into each creation, transforming raw cotton and silk into wearable art.
                            </p>
                            <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '15px', color: '#7C6B5D', lineHeight: 1.8, marginBottom: '32px', fontWeight: 300 }}>
                                We work directly with over 200 weaver families, ensuring fair wages and preserving craft traditions that are centuries old. When you wear Dharti, you wear a piece of India's soul.
                            </p>
                            <button
                                onClick={() => navigate('/template6/about')}
                                className="inline-flex items-center gap-2 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                                style={{
                                    backgroundColor: 'transparent',
                                    border: '2px solid #8B4513',
                                    color: '#8B4513',
                                    fontFamily: "'Nunito', sans-serif",
                                    fontWeight: 600,
                                    padding: '12px 28px',
                                    borderRadius: '2px',
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#8B4513';
                                    e.currentTarget.style.color = '#FFFFFF';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#8B4513';
                                }}
                            >
                                Read Our Story <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
                <style>{`
                    .dharti-story-grid {
                        grid-template-columns: 1fr;
                    }
                    @media (min-width: 768px) {
                        .dharti-story-grid {
                            grid-template-columns: 1fr 1fr;
                        }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 3: CATEGORIES ===== */}
            <section style={{ backgroundColor: '#FAF7F2' }} className="py-16 sm:py-20 lg:py-28">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '12px', fontWeight: 600, color: '#8B4513', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '12px' }}>
                            Browse By
                        </p>
                        <h2
                            style={{
                                fontFamily: "'Noto Serif Display', Georgia, serif",
                                fontSize: 'clamp(28px, 4vw, 40px)',
                                fontWeight: 400,
                                color: '#2C1810',
                                lineHeight: 1.3,
                            }}
                        >
                            Our Collections
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template6/shop')}
                                className="group cursor-pointer"
                                style={{ position: 'relative', overflow: 'hidden', borderRadius: '4px', aspectRatio: '3/4' }}
                            >
                                <img
                                    src={cat.image}
                                    alt={cat.name}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div
                                    className="absolute inset-0 transition-opacity duration-300"
                                    style={{ background: 'linear-gradient(to top, rgba(59, 26, 8, 0.8) 0%, rgba(59, 26, 8, 0.1) 60%)' }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                                    <h3
                                        style={{
                                            fontFamily: "'Noto Serif Display', Georgia, serif",
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            color: '#F5F0E8',
                                            margin: 0,
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        {cat.name}
                                    </h3>
                                    <p
                                        className="mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                        style={{ fontFamily: "'Nunito', sans-serif", fontSize: '12px', color: '#D4C5B0', letterSpacing: '1.5px', textTransform: 'uppercase' }}
                                    >
                                        Explore <ArrowRight className="w-3 h-3" />
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: PRODUCTS GRID (8 items) ===== */}
            <section style={{ backgroundColor: '#F5F0E8' }} className="py-16 sm:py-20 lg:py-28">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 lg:mb-16 gap-4">
                        <div>
                            <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '12px', fontWeight: 600, color: '#8B4513', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '12px' }}>
                                Curated For You
                            </p>
                            <h2
                                style={{
                                    fontFamily: "'Noto Serif Display', Georgia, serif",
                                    fontSize: 'clamp(28px, 4vw, 40px)',
                                    fontWeight: 400,
                                    color: '#2C1810',
                                    lineHeight: 1.3,
                                    margin: 0,
                                }}
                            >
                                Featured Collection
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate('/template6/shop')}
                            className="inline-flex items-center gap-2 text-sm transition-colors duration-300 self-start sm:self-auto"
                            style={{
                                fontFamily: "'Nunito', sans-serif",
                                fontWeight: 600,
                                color: '#8B4513',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                letterSpacing: '1.5px',
                                textTransform: 'uppercase',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#6B3410')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#8B4513')}
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template6/product/${product.id}`)}
                                className="group cursor-pointer"
                                style={{
                                    backgroundColor: '#FAF7F2',
                                    border: '1px solid #E8DFD1',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#8B4513';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 69, 19, 0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#E8DFD1';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                {/* Image */}
                                <div style={{ position: 'relative', aspectRatio: '3/4', overflow: 'hidden' }}>
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                    {product.badge && (
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: '10px',
                                                left: '10px',
                                                backgroundColor: '#8B4513',
                                                color: '#FFFFFF',
                                                fontFamily: "'Nunito', sans-serif",
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                letterSpacing: '1px',
                                                textTransform: 'uppercase',
                                                padding: '3px 10px',
                                                borderRadius: '2px',
                                            }}
                                        >
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            width: '32px',
                                            height: '32px',
                                            backgroundColor: 'rgba(250, 247, 242, 0.85)',
                                            border: 'none',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            backdropFilter: 'blur(4px)',
                                        }}
                                    >
                                        <Heart
                                            size={16}
                                            style={{
                                                color: wishlist[product.id] ? '#8B4513' : '#7C6B5D',
                                                fill: wishlist[product.id] ? '#8B4513' : 'none',
                                                transition: 'all 0.3s',
                                            }}
                                        />
                                    </button>
                                </div>

                                {/* Info */}
                                <div style={{ padding: '14px' }}>
                                    <h3
                                        style={{
                                            fontFamily: "'Nunito', sans-serif",
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: '#2C1810',
                                            margin: '0 0 6px 0',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {product.name}
                                    </h3>
                                    <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: '15px', fontWeight: 600, color: '#8B4513' }}>
                                        {product.price}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: ARTISAN HIGHLIGHT ===== */}
            <section style={{ backgroundColor: '#3B1A08' }} className="py-16 sm:py-20 lg:py-28">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '12px', fontWeight: 600, color: '#D4C5B0', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '12px' }}>
                            The Hands Behind The Craft
                        </p>
                        <h2
                            style={{
                                fontFamily: "'Noto Serif Display', Georgia, serif",
                                fontSize: 'clamp(28px, 4vw, 40px)',
                                fontWeight: 400,
                                color: '#F5F0E8',
                                lineHeight: 1.3,
                            }}
                        >
                            Meet Our Artisans
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {[
                            { name: 'Lakshmi Devi', craft: 'Chanderi Weaving', location: 'Madhya Pradesh', years: '35+ years', desc: 'A master of the delicate Chanderi technique, Lakshmi Devi creates sarees that shimmer like moonlight on water.' },
                            { name: 'Ramesh Kumar', craft: 'Block Printing', location: 'Rajasthan', years: '28 years', desc: 'Using hand-carved teak blocks passed down through four generations, Ramesh creates intricate Ajrakh and Bagru prints.' },
                            { name: 'Padma Rani', craft: 'Ikat Dyeing', location: 'Telangana', years: '22 years', desc: 'Padma specialises in the resist-dyeing Ikat technique, tying and dyeing threads before they meet the loom.' },
                        ].map((artisan, index) => (
                            <div
                                key={index}
                                style={{
                                    backgroundColor: 'rgba(139, 69, 19, 0.15)',
                                    border: '1px solid #6B3410',
                                    borderRadius: '4px',
                                    padding: '32px',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#8B4513', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '18px', color: '#F5F0E8', fontWeight: 600 }}>
                                            {artisan.name.charAt(0)}
                                        </span>
                                    </div>
                                    <div>
                                        <h3 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '16px', fontWeight: 600, color: '#F5F0E8', margin: 0 }}>
                                            {artisan.name}
                                        </h3>
                                        <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '12px', color: '#D4C5B0', margin: 0 }}>
                                            {artisan.craft} &middot; {artisan.location}
                                        </p>
                                    </div>
                                </div>
                                <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '14px', color: '#D4C5B0', lineHeight: 1.7, marginBottom: '16px', fontWeight: 300 }}>
                                    {artisan.desc}
                                </p>
                                <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: '11px', fontWeight: 600, color: '#8B4513', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                                    {artisan.years} of craft
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: TRUST BADGES ===== */}
            <section style={{ backgroundColor: '#FAF7F2', borderTop: '1px solid #E8DFD1', borderBottom: '1px solid #E8DFD1' }} className="py-12 lg:py-16">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { icon: Truck, title: 'Free Shipping', desc: 'On orders above \u20B91,499' },
                            { icon: RefreshCw, title: 'Easy Returns', desc: '7-day exchange policy' },
                            { icon: Shield, title: 'Authentic Handloom', desc: 'GI tagged products' },
                            { icon: Heart, title: 'Artisan Direct', desc: 'Fair trade certified' },
                        ].map((item, i) => (
                            <div key={i} className="text-center">
                                <item.icon size={28} style={{ color: '#8B4513', margin: '0 auto 12px' }} />
                                <h4 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '15px', fontWeight: 600, color: '#2C1810', marginBottom: '4px' }}>
                                    {item.title}
                                </h4>
                                <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '13px', color: '#7C6B5D', margin: 0, fontWeight: 300 }}>
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: TESTIMONIALS ===== */}
            <section style={{ backgroundColor: '#F5F0E8' }} className="py-16 sm:py-20 lg:py-28">
                <div className="max-w-[800px] mx-auto px-5 sm:px-8 text-center">
                    <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '12px', fontWeight: 600, color: '#8B4513', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '12px' }}>
                        Voices Of Our Community
                    </p>
                    <h2
                        style={{
                            fontFamily: "'Noto Serif Display', Georgia, serif",
                            fontSize: 'clamp(28px, 4vw, 36px)',
                            fontWeight: 400,
                            color: '#2C1810',
                            lineHeight: 1.3,
                            marginBottom: '48px',
                        }}
                    >
                        What Our Customers Say
                    </h2>

                    <div
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        style={{ minHeight: '200px', position: 'relative' }}
                    >
                        <Quote size={32} style={{ color: '#8B4513', opacity: 0.3, margin: '0 auto 20px' }} />
                        <p
                            style={{
                                fontFamily: "'Noto Serif Display', Georgia, serif",
                                fontSize: '18px',
                                fontWeight: 400,
                                color: '#2C1810',
                                lineHeight: 1.8,
                                fontStyle: 'italic',
                                marginBottom: '24px',
                            }}
                        >
                            "{testimonials[activeTestimonial].text}"
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '8px' }}>
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={14} style={{ color: '#8B4513', fill: '#8B4513' }} />
                            ))}
                        </div>
                        <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '14px', fontWeight: 600, color: '#2C1810', margin: '0 0 4px 0' }}>
                            {testimonials[activeTestimonial].name}
                        </p>
                        <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '13px', color: '#7C6B5D', margin: 0, fontWeight: 300 }}>
                            {testimonials[activeTestimonial].city}
                        </p>

                        {/* Dots */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '32px' }}>
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    style={{
                                        width: i === activeTestimonial ? '24px' : '8px',
                                        height: '8px',
                                        borderRadius: '4px',
                                        backgroundColor: i === activeTestimonial ? '#8B4513' : '#D4C5B0',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        padding: 0,
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 8: NEWSLETTER ===== */}
            <section style={{ backgroundColor: '#3B1A08' }} className="py-16 sm:py-20 lg:py-24">
                <div className="max-w-[700px] mx-auto px-5 sm:px-8 text-center">
                    <h2
                        style={{
                            fontFamily: "'Noto Serif Display', Georgia, serif",
                            fontSize: 'clamp(24px, 3.5vw, 32px)',
                            fontWeight: 400,
                            color: '#F5F0E8',
                            letterSpacing: '1px',
                            marginBottom: '8px',
                        }}
                    >
                        Stay Connected to the Loom
                    </h2>
                    <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '14px', color: '#D4C5B0', marginBottom: '32px', fontWeight: 300 }}>
                        New arrivals, artisan stories, and exclusive handloom drops delivered to your inbox.
                    </p>
                    <form
                        onSubmit={handleSubscribe}
                        className="dharti-home-newsletter"
                        style={{ display: 'flex', gap: '0', maxWidth: '480px', margin: '0 auto' }}
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                            style={{
                                flex: 1,
                                padding: '14px 20px',
                                backgroundColor: 'rgba(139, 69, 19, 0.2)',
                                border: '1px solid #6B3410',
                                borderRight: 'none',
                                borderRadius: '2px 0 0 2px',
                                color: '#F5F0E8',
                                fontFamily: "'Nunito', sans-serif",
                                fontSize: '14px',
                                outline: 'none',
                            }}
                        />
                        <button
                            type="submit"
                            style={{
                                padding: '14px 28px',
                                backgroundColor: '#8B4513',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '0 2px 2px 0',
                                fontFamily: "'Nunito', sans-serif",
                                fontSize: '13px',
                                fontWeight: 600,
                                letterSpacing: '1.5px',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#6B3410')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = '#8B4513')}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
                <style>{`
                    @media (max-width: 480px) {
                        .dharti-home-newsletter {
                            flex-direction: column !important;
                        }
                        .dharti-home-newsletter input {
                            border-right: 1px solid #6B3410 !important;
                            border-radius: 2px !important;
                        }
                        .dharti-home-newsletter button {
                            border-radius: 2px !important;
                            margin-top: 8px;
                        }
                    }
                `}</style>
            </section>
        </div>
    );
};

export default Template6;
